import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogPosts, type BlogPost as BlogPostData } from '../blogPosts';

export const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p: BlogPostData) => p.slug === slug);

  if (!post) {
    return (
      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">
            Article introuvable
          </h1>
          <p className="text-sm text-slate-600 mb-4">
            L’article que vous recherchez n’existe plus ou a été déplacé.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 transition-colors"
          >
            Revenir au blog
          </Link>
        </div>
      </div>
    );
  }

  type ContentBlock =
    | { type: 'paragraph'; text: string }
    | { type: 'heading2'; text: string }
    | { type: 'heading3'; text: string }
    | { type: 'list'; items: string[] };

  const rawBlocks: string[] = post.body.split('\n\n');
  const blocks: ContentBlock[] = [];
  let currentList: string[] | null = null;

  const flushList = () => {
    if (currentList && currentList.length > 0) {
      blocks.push({ type: 'list', items: currentList });
      currentList = null;
    }
  };

  for (const raw of rawBlocks) {
    const line = raw.trim();
    if (!line) continue;

    if (line.startsWith('## ')) {
      flushList();
      blocks.push({ type: 'heading2', text: line.slice(3) });
    } else if (line.startsWith('### ')) {
      flushList();
      blocks.push({ type: 'heading3', text: line.slice(4) });
    } else if (line.startsWith('- ')) {
      if (!currentList) currentList = [];
      currentList.push(line.slice(2));
    } else {
      flushList();
      blocks.push({ type: 'paragraph', text: line });
    }
  }

  flushList();
  const relatedPosts: BlogPostData[] = blogPosts
    .filter((p: BlogPostData) => p.slug !== post.slug)
    .slice(0, 3);

  let cityPath: string | undefined;
  if (post.city === 'Nice') {
    cityPath = '/nice-06';
  } else if (post.city === 'Toulon') {
    cityPath = '/var-toulon';
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      <article className="max-w-3xl mx-auto px-4 py-10 lg:py-14">
        <nav className="mb-4 text-[11px] text-slate-500">
          <Link to="/" className="hover:text-primary transition-colors">
            Accueil
          </Link>{' '}
          <span>/</span>{' '}
          <Link to="/blog" className="hover:text-primary transition-colors">
            Blog
          </Link>{' '}
          <span>/</span>{' '}
          <span className="text-slate-700">{post.title}</span>
        </nav>

        <p className="text-[11px] uppercase tracking-wide text-primary font-semibold mb-2">
          {post.category}
          {post.city ? ` · ${post.city}` : ''}
        </p>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
          {post.h1}
        </h1>
        <p className="text-xs text-slate-500 mb-2">
          Publié le {post.publishedAt} · {post.readingTime} de lecture
        </p>

        <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4">
          {post.subheading}
        </h2>

        <div className="mt-2 mb-6 rounded-2xl border border-slate-200 bg-slate-900/95 p-4 text-xs text-slate-100">
          <h2 className="text-sm font-semibold mb-2">
            Ce que vous allez decouvrir dans cet article
          </h2>
          <ul className="list-disc list-inside space-y-1">
            <li>les etapes d une intervention de nettoyage logement insalubre 06 / 83</li>
            <li>les principaux facteurs qui font varier le prix</li>
            <li>comment demander un devis rapide avec photos</li>
          </ul>
        </div>

        <div className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <img
            src={post.image}
            alt={post.imageAlt}
            className="w-full h-56 object-cover"
            loading="lazy"
          />
        </div>

        <div className="prose prose-sm max-w-none text-slate-700">
          {blocks.map((block: ContentBlock, index: number) => {
            if (block.type === 'paragraph') {
              return (
                <p key={index} className="mb-3">
                  {block.text}
                </p>
              );
            }

            if (block.type === 'heading2') {
              return (
                <h2
                  key={index}
                  className="text-lg sm:text-xl font-semibold text-slate-900 mt-6 mb-3"
                >
                  {block.text}
                </h2>
              );
            }

            if (block.type === 'heading3') {
              return (
                <h3
                  key={index}
                  className="text-base sm:text-lg font-semibold text-slate-900 mt-4 mb-2"
                >
                  {block.text}
                </h3>
              );
            }

            if (block.type === 'list') {
              return (
                <ul
                  key={index}
                  className="mb-3 list-disc list-inside space-y-1"
                >
                  {block.items.map((item: string, itemIndex: number) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ul>
              );
            }

            return null;
          })}
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {post.keywords.map((keyword: string) => (
            <span
              key={keyword}
              className="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-[11px] text-slate-700"
            >
              {keyword}
            </span>
          ))}
        </div>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-700">
          <h2 className="text-sm font-semibold text-slate-900 mb-2">
            Besoin d un devis nettoyage logement insalubre ?
          </h2>
          <p className="mb-3">
            Pour obtenir un tarif precis et un delai d intervention, envoyez vos
            photos et quelques informations sur le logement (surface, ville,
            situation) via notre page devis, ou consultez nos fourchettes de
            prix moyennes.
          </p>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/devis"
              className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-blue-800 transition-colors"
            >
              Demander un devis en ligne
            </Link>
            <Link
              to="/tarifs"
              className="inline-flex items-center justify-center rounded-md border border-slate-300 px-3 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-100 transition-colors"
            >
              Voir les tarifs moyens
            </Link>
          </div>
        </div>

        {cityPath && (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-900/95 p-4 text-xs text-slate-100">
            <h3 className="text-sm font-semibold mb-2">
              Intervention nettoyage logement insalubre a {post.city}
            </h3>
            <p className="mb-3">
              Decouvrez notre page dediee au nettoyage de logements insalubres a{' '}
              {post.city} : exemples d interventions, zone couverte et
              organisation pratique.
            </p>
            <Link
              to={cityPath}
              className="inline-flex items-center justify-center rounded-md bg-mint px-3 py-1.5 text-[11px] font-semibold text-slate-900 hover:bg-emerald-400 transition-colors"
            >
              Voir les interventions a {post.city}
            </Link>
          </div>
        )}

        {relatedPosts.length > 0 && (
          <section className="mt-8 border-t border-slate-200 pt-6">
            <h2 className="text-sm font-semibold text-slate-900 mb-3">
              Autres articles sur le nettoyage logement insalubre
            </h2>
            <div className="grid gap-3 md:grid-cols-3">
              {relatedPosts.map((related: BlogPostData) => (
                <article
                  key={related.slug}
                  className="rounded-xl border border-slate-200 bg-white p-3 text-[11px] text-slate-700"
                >
                  <p className="text-[10px] uppercase tracking-wide text-primary font-semibold mb-1">
                    {related.category}
                    {related.city ? ` Aú ${related.city}` : ''}
                  </p>
                  <Link
                    to={`/blog/${related.slug}`}
                    className="block font-semibold text-slate-900 mb-1 hover:text-primary transition-colors"
                  >
                    {related.title}
                  </Link>
                  <p className="line-clamp-3">{related.description}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        <div className="mt-8 text-xs text-slate-500">
          <Link
            to="/blog"
            className="inline-flex items-center justify-center rounded-md border border-slate-300 px-3 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-100 transition-colors"
          >
            ← Retour aux articles de blog
          </Link>
        </div>
      </article>
    </div>
  );
};
